import { ApplicationConfig, importProvidersFrom, enableProdMode, isDevMode } from '@angular/core';
import { provideRouter, withViewTransitions, withInMemoryScrolling } from '@angular/router';
import { provideHttpClient,  withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserModule, Title } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { localStorageSync } from 'ngrx-store-localstorage';
import { provideEffects } from '@ngrx/effects';
import { routes } from './src/app/app.route';
import { provideStoreDevtools } from '@ngrx/store-devtools';


// Sync reducers with localStorage
export function localStorageSyncReducer(reducer: any): any {
    return localStorageSync({
        keys: ['auth'],
        rehydrate: true,
    })(reducer);
}

const metaReducers: MetaReducer[] = [localStorageSyncReducer];

// store
import { MetaReducer, provideStore } from '@ngrx/store';
import { indexReducer } from './src/app/store/index.reducer';

// i18n
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';

// base url
export function getBaseUrl() {
    return document.getElementsByTagName('base')[0].href;
}

// service
import { AppService } from './src/app/core/services/app.service';

// environment
import { environment } from './src/environments/environment';
if (environment.production) {
    enableProdMode();
}

// perfect-scrollbar
import { provideScrollbarOptions } from 'ngx-scrollbar';

import { authReducer } from './src/app/store/reducers/auth.reducers';
import { permissionReducer } from './src/app/store/reducers/permission.reducer';
import { tokenReducer } from './src/app/store/reducers/token.reducer';
import { loadingReducer } from './src/app/store/reducers/loading.reducer';
import { languageReducer } from './src/app/store/reducers/language.reducer';
import { LanguageEffects } from './src/app/store/effects/language.effects';
import { AuthEffects } from './src/app/store/effects/auth.effects';
import { InterceptorService } from './src/app/core/interceptors/interceptor.service';
import { PERMISSION_SELECTOR_TOKEN, REVIEW_STATE_ACCESSOR } from '@sukna/shared';
import { selectAllPermissionCodes } from './src/app/store/selectors/permission.selectors';
import { ReviewStateService } from './src/app/core/services/review-state.service';

export const appConfig: ApplicationConfig = {
    providers: [
        Title,
        AppService,
        importProvidersFrom(BrowserModule, CommonModule, FormsModule),
        provideRouter(
            routes,
            withViewTransitions(),
            withInMemoryScrolling({
                scrollPositionRestoration: 'enabled',
            }),
        ),
        provideHttpClient(withInterceptorsFromDi()),
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: './assets/i18n/',
                suffix: '.json',
                useHttpBackend: true,
            }),
            fallbackLang: 'en',
        }),
        { provide: 'BASE_URL', useFactory: getBaseUrl, deps: [] },
        provideScrollbarOptions({
            visibility: 'hover',
            appearance: 'compact',
        }),
        provideAnimations(), 
        provideStore(
            {
                index: indexReducer,
                auth: authReducer,
                permission: permissionReducer,
                token: tokenReducer,
                loading: loadingReducer,
                language: languageReducer,
            },
            { metaReducers }
        ),

        provideEffects([LanguageEffects, AuthEffects]),
        {
            provide: PERMISSION_SELECTOR_TOKEN,
            useValue: selectAllPermissionCodes,
        },
        {
            provide: REVIEW_STATE_ACCESSOR,
            useExisting: ReviewStateService,
        },

        provideStoreDevtools({
            maxAge: 25,
            logOnly: !isDevMode(),
            autoPause: true,
            trace: false,
            traceLimit: 75,
        }),
        {
            provide: HTTP_INTERCEPTORS,
            useClass: InterceptorService,
            multi: true,
        },
    ],
};
